import React, { useState } from 'react';

export default function AAMissileRotationTest() {
  const [rotation, setRotation] = useState(0);
  const [selectedMissile, setSelectedMissile] = useState('USA');
  
  const missiles = {
    'USA': { name: 'Stinger', sprite: '/assets/aa-missiles/stinger.png' },
    'RUS': { name: 'Strela-2', sprite: '/assets/aa-missiles/strela-2.png' },
    'CHN': { name: 'FN-6', sprite: '/assets/aa-missiles/fn-6.png' },
    'NK': { name: 'HT-16PGJ', sprite: '/assets/aa-missiles/ht-16pgj.png' },
    'EU': { name: 'Mistral', sprite: '/assets/aa-missiles/mistral.png' }
  };

  // Target positions at different angles (relative to center)
  const targets = [
    { angle: 0, label: '0° (Right)', x: 40, y: 0 },
    { angle: 45, label: '45° (Top-Right)', x: 28, y: -28 },
    { angle: 90, label: '90° (Up)', x: 0, y: -40 },
    { angle: 135, label: '135° (Top-Left)', x: -28, y: -28 },
    { angle: 180, label: '180° (Left)', x: -40, y: 0 },
    { angle: 225, label: '225° (Bottom-Left)', x: -28, y: 28 },
    { angle: 270, label: '270° (Down)', x: 0, y: 40 },
    { angle: 315, label: '315° (Bottom-Right)', x: 28, y: 28 }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2">🚀 AA Missile Rotation Test</h1>
          <p className="text-gray-400">Adjust rotation to make the missile nose point at targets</p>
        </div>

        {/* Missile Selector */}
        <div className="bg-gray-800 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Select Missile</h2>
          <div className="grid grid-cols-5 gap-4">
            {Object.entries(missiles).map(([code, missile]) => (
              <button
                key={code}
                onClick={() => setSelectedMissile(code)}
                className={`p-4 rounded-lg border-2 transition-all ${
                  selectedMissile === code
                    ? 'border-blue-500 bg-blue-500/20'
                    : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                }`}
              >
                <div className="text-sm font-bold mb-1">{code}</div>
                <div className="text-xs text-gray-400">{missile.name}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Rotation Controls */}
        <div className="bg-gray-800 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Rotation Control</h2>
          
          <div className="space-y-4">
            {/* Slider */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Rotation: <span className="text-blue-400 font-bold">{rotation}°</span>
              </label>
              <input
                type="range"
                min="-180"
                max="180"
                value={rotation}
                onChange={(e) => setRotation(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>-180°</span>
                <span>0°</span>
                <span>+180°</span>
              </div>
            </div>

            {/* Manual Input */}
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-2">Manual Input</label>
                <input
                  type="number"
                  min="-180"
                  max="180"
                  value={rotation}
                  onChange={(e) => setRotation(parseInt(e.target.value) || 0)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                />
              </div>
              
              {/* Quick Buttons */}
              <div className="flex gap-2 items-end">
                <button
                  onClick={() => setRotation(0)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded border border-gray-600"
                >
                  Reset (0°)
                </button>
                <button
                  onClick={() => setRotation(rotation - 5)}
                  className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded border border-gray-600"
                >
                  -5°
                </button>
                <button
                  onClick={() => setRotation(rotation + 5)}
                  className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded border border-gray-600"
                >
                  +5°
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Visual Test Area */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-bold mb-4">Visual Test</h2>
          
          {/* Center container with missile and targets */}
          <div className="relative bg-gray-900 rounded-lg" style={{ height: '600px' }}>
            
            {/* Target dots */}
            {targets.map((target, idx) => (
              <div key={`target-${target.x}-${target.y}-${idx}`}>
                {/* Dot */}
                <div
                  className="absolute w-6 h-6 bg-red-500 rounded-full border-2 border-white"
                  style={{
                    left: `calc(50% + ${target.x}vh)`,
                    top: `calc(50% + ${target.y}vh)`,
                    transform: 'translate(-50%, -50%)',
                    zIndex: 10
                  }}
                />
                
                {/* Label */}
                <div
                  className="absolute text-xs font-bold text-white bg-red-500/80 px-2 py-1 rounded whitespace-nowrap"
                  style={{
                    left: `calc(50% + ${target.x * 1.3}vh)`,
                    top: `calc(50% + ${target.y * 1.3}vh)`,
                    transform: 'translate(-50%, -50%)',
                    zIndex: 11
                  }}
                >
                  {target.label}
                </div>
              </div>
            ))}

            {/* Center crosshair */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
              <div className="absolute w-px h-8 bg-green-500 -translate-x-1/2 -translate-y-4"></div>
              <div className="absolute w-8 h-px bg-green-500 -translate-x-4 -translate-y-1/2"></div>
            </div>

            {/* AA Missile - Centered */}
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-30"
              style={{
                transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
                transformOrigin: 'center center'
              }}
            >
              <img
                src={missiles[selectedMissile].sprite}
                alt={missiles[selectedMissile].name}
                className="w-32 h-auto"
                style={{
                  filter: 'drop-shadow(0 0 20px rgba(255,140,0,1)) brightness(1.2)',
                }}
              />
            </div>

            {/* Info overlay */}
            <div className="absolute top-4 left-4 bg-black/70 backdrop-blur-sm rounded-lg p-4 z-40">
              <div className="text-sm space-y-1">
                <div><span className="text-gray-400">Missile:</span> <span className="font-bold">{missiles[selectedMissile].name}</span></div>
                <div><span className="text-gray-400">Rotation:</span> <span className="font-bold text-blue-400">{rotation}°</span></div>
                <div className="text-xs text-gray-500 mt-2">
                  Rotate until the missile <span className="text-yellow-400">nose</span> points at a red target
                </div>
              </div>
            </div>

            {/* Instructions */}
            <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur-sm rounded-lg p-4 z-40 max-w-xs">
              <div className="text-xs space-y-2">
                <div className="font-bold text-yellow-400">📝 Instructions:</div>
                <div>1. Pick a target (e.g., "45° Top-Right")</div>
                <div>2. Adjust rotation until missile nose points at it</div>
                <div>3. Note the rotation value that works</div>
                <div>4. Test multiple angles to find the pattern</div>
                <div className="text-gray-500 mt-2">
                  The correct angle will tell us how to fix the code!
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Results Section */}
        <div className="bg-gray-800 rounded-lg p-6 mt-6">
          <h2 className="text-xl font-bold mb-4">📊 Results to Share</h2>
          <div className="bg-gray-900 rounded p-4 font-mono text-sm">
            <div className="text-green-400">// Copy this and send it back:</div>
            <div className="text-white mt-2">
              Missile: {missiles[selectedMissile].name} ({selectedMissile})
              <br />
              When pointing at 45° target (top-right), rotation needed: <span className="text-yellow-400">{rotation}°</span>
              <br />
              <br />
              <span className="text-gray-500">// Test a few different targets and tell me the rotation values</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
